import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { publicAPI, leaderboardAPI } from '@/lib/api';
import { formatTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  BookOpen, Trophy, Medal, Clock, User, ArrowRight, 
  Crown, Sparkles, TrendingUp, Award, Flame, Globe, Target
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Program yang konsisten di seluruh aplikasi
const PROGRAMS = [
  'UTBK-SNBT',
  'CPNS',
  'TNI',
  'POLRI',
  'SEKOLAH KEDINASAN',
  'BUMN',
  'TKA',
  'SMA Favorit'
];

export default function RankingPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('tryout');
  const [tryouts, setTryouts] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [selectedTryout, setSelectedTryout] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLb, setLoadingLb] = useState(false);
  const [loadingGlobal, setLoadingGlobal] = useState(false);

  useEffect(() => {
    loadTryouts();
    loadGlobalLeaderboard();
  }, [selectedProgram]);

  useEffect(() => {
    if (selectedTryout) {
      loadLeaderboard(selectedTryout);
    }
  }, [selectedTryout]);

  const loadGlobalLeaderboard = async () => {
    try {
      setLoadingGlobal(true);
      const params = selectedProgram !== 'all' ? { program: selectedProgram } : {};
      const res = await leaderboardAPI.getGlobal(params);
      setGlobalLeaderboard(res.data || []);
    } catch (err) {
      console.error('Failed to load global leaderboard:', err);
      setGlobalLeaderboard([]);
    } finally {
      setLoadingGlobal(false);
    }
  };

  const loadTryouts = async () => {
    try {
      setLoading(true);
      const res = await publicAPI.getTryouts(selectedProgram !== 'all' ? selectedProgram : null);
      const data = res.data || [];
      setTryouts(data);
      if (data.length > 0) {
        setSelectedTryout(data[0].id);
      } else {
        setSelectedTryout(null);
        setLeaderboard([]);
      }
    } catch (err) {
      console.error('Failed to load tryouts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async (tryoutId) => {
    setLoadingLb(true);
    try {
      const res = await publicAPI.getLeaderboard(tryoutId);
      setLeaderboard(res.data || []);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
      setLeaderboard([]);
    } finally {
      setLoadingLb(false);
    }
  };

  const selectedTryoutData = tryouts.find(t => t.id === selectedTryout);

  const getMedalIcon = (rank) => {
    if (rank === 1) return <Crown className="w-8 h-8 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-8 h-8 text-slate-400" />;
    if (rank === 3) return <Medal className="w-8 h-8 text-amber-600" />;
    return null;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 border-yellow-300 shadow-lg shadow-yellow-100';
    if (rank === 2) return 'bg-gradient-to-r from-slate-50 via-slate-100 to-slate-50 border-slate-300';
    if (rank === 3) return 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-amber-300';
    return 'bg-white hover:bg-slate-50 border-slate-200';
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-blue-600';
    if (percentage >= 60) return 'text-yellow-600';
    return 'text-red-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="ranking-page">
      {/* Header with Mobile Navigation */}
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-[#4472C4] via-[#5a8dd6] to-[#4472C4] text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 mb-4 px-4 py-1.5">
            <Trophy className="w-3 h-3 mr-1.5" />
            Leaderboard
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Papan <span className="text-yellow-300">Peringkat</span>
          </h1>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            Lihat posisimu di antara peserta terbaik dari seluruh Indonesia
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-slate-500 font-medium">Memuat data...</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Filters */}
              <div className="lg:col-span-1">
                <Card className="sticky top-24 border-slate-200">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <TrendingUp className="w-5 h-5 text-[#4472C4]" />
                      Filter Peringkat
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Program</label>
                      <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                        <SelectTrigger className="w-full" data-testid="program-filter">
                          <SelectValue placeholder="Semua Program" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Semua Program</SelectItem>
                          {PROGRAMS.map(prog => (
                            <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {tryouts.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Try Out</label>
                        <Select value={selectedTryout} onValueChange={setSelectedTryout}>
                          <SelectTrigger className="w-full" data-testid="tryout-filter">
                            <SelectValue placeholder="Pilih Try Out" />
                          </SelectTrigger>
                          <SelectContent>
                            {tryouts.map(to => (
                              <SelectItem key={to.id} value={to.id}>{to.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedTryoutData && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <p className="text-sm font-medium text-slate-700">{selectedTryoutData.title}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {selectedTryoutData.question_count} soal • {selectedTryoutData.duration_minutes} menit
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {!user && (
                      <div className="pt-4 border-t border-slate-200">
                        <p className="text-sm text-slate-600 mb-3">Ingin masuk leaderboard?</p>
                        <Link to="/register">
                          <Button className="w-full bg-[#FF6B35] hover:bg-[#e55a2a]">
                            <Flame className="w-4 h-4 mr-2" />
                            Ikut Berkompetisi
                          </Button>
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Leaderboard */}
              <div className="lg:col-span-2">
                {tryouts.length === 0 ? (
                  <Card className="border-slate-200">
                    <CardContent className="py-16 text-center">
                      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Trophy className="w-10 h-10 text-slate-300" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">Belum Ada Try Out</h3>
                      <p className="text-slate-500 mb-6">Pilih program lain atau kerjakan try out terlebih dahulu</p>
                      <Link to="/tryouts">
                        <Button className="bg-[#4472C4] hover:bg-[#3861a8]">
                          Lihat Try Out
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ) : loadingLb ? (
                  <Card className="border-slate-200">
                    <CardContent className="py-16 text-center">
                      <div className="w-12 h-12 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="mt-4 text-slate-500">Memuat leaderboard...</p>
                    </CardContent>
                  </Card>
                ) : leaderboard.length === 0 ? (
                  <Card className="border-slate-200">
                    <CardContent className="py-16 text-center">
                      <div className="w-20 h-20 bg-gradient-to-br from-yellow-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Award className="w-10 h-10 text-yellow-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">Jadilah yang Pertama!</h3>
                      <p className="text-slate-500 mb-6">Belum ada peserta untuk try out ini</p>
                      <Link to={user ? `/exam/${selectedTryout}` : '/register'}>
                        <Button className="bg-gradient-to-r from-[#FF6B35] to-[#ff8555] hover:opacity-90">
                          <Sparkles className="w-4 h-4 mr-2" />
                          {user ? 'Kerjakan Sekarang' : 'Daftar & Kerjakan'}
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {/* Top 3 Podium */}
                    {leaderboard.length >= 3 && (
                      <div className="grid grid-cols-3 gap-4 mb-8">
                        {/* 2nd Place */}
                        <div className="order-1 pt-8">
                          <Card className="border-slate-300 bg-gradient-to-b from-slate-50 to-slate-100 text-center">
                            <CardContent className="pt-6 pb-4">
                              <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Medal className="w-6 h-6 text-slate-500" />
                              </div>
                              <div className="text-2xl font-bold text-slate-400 mb-1">#2</div>
                              <p className="font-semibold text-slate-900 text-sm truncate">{leaderboard[1]?.user_name}</p>
                              <p className="text-lg font-bold text-slate-600">{leaderboard[1]?.percentage}%</p>
                            </CardContent>
                          </Card>
                        </div>
                        {/* 1st Place */}
                        <div className="order-2">
                          <Card className="border-yellow-300 bg-gradient-to-b from-yellow-50 to-amber-100 text-center shadow-lg shadow-yellow-100">
                            <CardContent className="pt-6 pb-4">
                              <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                                <Crown className="w-7 h-7 text-white" />
                              </div>
                              <div className="text-3xl font-bold text-yellow-500 mb-1">#1</div>
                              <p className="font-semibold text-slate-900 truncate">{leaderboard[0]?.user_name}</p>
                              <p className="text-xl font-bold text-amber-600">{leaderboard[0]?.percentage}%</p>
                            </CardContent>
                          </Card>
                        </div>
                        {/* 3rd Place */}
                        <div className="order-3 pt-12">
                          <Card className="border-amber-300 bg-gradient-to-b from-amber-50 to-orange-100 text-center">
                            <CardContent className="pt-6 pb-4">
                              <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Medal className="w-5 h-5 text-amber-600" />
                              </div>
                              <div className="text-xl font-bold text-amber-500 mb-1">#3</div>
                              <p className="font-semibold text-slate-900 text-sm truncate">{leaderboard[2]?.user_name}</p>
                              <p className="text-lg font-bold text-amber-600">{leaderboard[2]?.percentage}%</p>
                            </CardContent>
                          </Card>
                        </div>
                      </div>
                    )}

                    {/* Full List */}
                    <Card className="border-slate-200 overflow-hidden">
                      <CardHeader className="bg-slate-50 border-b border-slate-200">
                        <CardTitle className="flex items-center gap-2">
                          <Trophy className="w-5 h-5 text-yellow-500" />
                          Daftar Peringkat Lengkap
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="divide-y divide-slate-100">
                          {leaderboard.map((entry, i) => (
                            <div 
                              key={i}
                              className={cn(
                                "flex items-center justify-between p-4 transition-all",
                                getRankBg(entry.rank),
                                user?.name === entry.user_name && "ring-2 ring-[#4472C4] ring-inset"
                              )}
                              data-testid={`rank-${entry.rank}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-14 h-14 flex items-center justify-center">
                                  {entry.rank <= 3 ? (
                                    getMedalIcon(entry.rank)
                                  ) : (
                                    <span className="text-2xl font-bold text-slate-400">#{entry.rank}</span>
                                  )}
                                </div>
                                <div>
                                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                                    {entry.user_name}
                                    {user?.name === entry.user_name && (
                                      <Badge className="bg-[#4472C4] text-white text-xs">Anda</Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-8">
                                <div className="text-right">
                                  <div className={cn("text-2xl font-bold", getScoreColor(entry.percentage))}>
                                    {entry.percentage}%
                                  </div>
                                  <div className="text-xs text-slate-500">Skor</div>
                                </div>
                                <div className="text-right min-w-[90px]">
                                  <div className="font-mono text-slate-700 flex items-center gap-1">
                                    <Clock className="w-4 h-4 text-slate-400" />
                                    {formatTime(entry.time_spent_seconds)}
                                  </div>
                                  <div className="text-xs text-slate-500">Waktu</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
